import React from "react";
import BoxWrap from "../shared/box-wrap";
import ClearIcon from "@mui/icons-material/Clear";
import CustomButton from "../shared/custom-button";
import Header from "../shared/header";
import SaveIcon from "@mui/icons-material/Save";
import useOnlineStatusStore from "../../stores/store-online";
import { Grid, useMediaQuery } from "@mui/material";
import { Formik } from "formik";
import { InfoModel } from "../../models/info.model";
import { showErrorAlert, showSuccessAlert } from "../shared/alerts";
import { useNavigate } from "react-router-dom";
import SelectField from "../shared/select-field";
import { MicroSettingsForm } from "../../models/micro-settings.model";
import { useFetchMicrobiologicalOptions, useFetchMicrobiologicalSettings, useMicrobiologicalSettings } from "../../api/sub-tasks";
import Loader from "../shared/loader";

/**
 * A component that handles the microbiological settings form.
 * Features include:
 * - Form for managing microbiological settings
 * - Online/offline status handling
 * - Form validation using Formik
 * - Responsive layout
 * - Loading state handling
 * - Error handling and success notifications
 * 
 * @component
 * @returns {JSX.Element} The rendered microbiological settings form
 * 
 * @example
 * ```tsx
 * <MicrobiologicalSettings />
 * ```
 */
const MicrobiologicalSettings = () => {
	const isMobile = useMediaQuery("(max-width: 600px)");
	const { isOnline } = useOnlineStatusStore();
	const navigate = useNavigate();

	const affirmativeNegativeList: InfoModel[] = [
		{ id: 1, name: "Ja" },
		{ id: 0, name: "Nein" },
	];

	// Fetch micro options data
	const { data } = useFetchMicrobiologicalOptions();
	const tranzportTime = data?.tranzport_time;

	// Fetch current micro settings data and initialize form data
	const { data: microSettingsData, isLoading } = useFetchMicrobiologicalSettings();
	const formInitialValues: Partial<MicroSettingsForm> = {
		...microSettingsData,
		tranzport_time: microSettingsData?.tranzport_time?.id
	};

	const microbiologicalSettingsMutation = useMicrobiologicalSettings();

	/**
	 * Handles the form submission for microbiological settings.
	 * 
	 * @param {any} values - The form values to be submitted
	 * @returns {Promise<void>}
	 */
	const handleFormSubmit = async (values: any) => {
		try {
			if (!isOnline) {
				await showSuccessAlert(
					"Ihr Gerät ist derzeit offline. Keine Sorge, alle ausstehenden Daten werden automatisch synchronisiert, sobald eine Internetverbindung hergestellt ist. Bleiben Sie dran!"
				);
				navigate(-1);
			}

			await microbiologicalSettingsMutation.mutateAsync(values);
			await showSuccessAlert("Unteraufgabe erfolgreich aktualisiert");
			navigate(-1);

		} catch (error: any) {
			const data = error.response?.data;
			showErrorAlert(data?.message);
		}
	};

	if (isLoading) {
		return <Loader />
	}

	return (
		<>
			<Formik initialValues={formInitialValues} onSubmit={handleFormSubmit}>
				{({
					values,
					resetForm,
					handleSubmit,
					isSubmitting,
				}) => {
					return (
						<form onSubmit={handleSubmit} style={{ marginTop: isMobile ? "20px" : "40px" }}>
							<Header title="UEG Standarteinstellungen ändern" />
							<BoxWrap mt={4} px={2} py={4}>
								<Grid container alignItems="flex-start" spacing={2}>
									<Grid item xs={12} md={12} lg={12} py={1}>
										<Header title="Allgemein" />
									</Grid>
									<Grid item xs={12} md={12} lg={6} py={1}>
										<SelectField
											name="tranzport_time"
											label="Transportzeit"
											keyItem="name"
											value={values.tranzport_time ?? ''}
											options={tranzportTime}
										/>
									</Grid>
									<Grid item xs={12} md={12} lg={6} py={1}>
										<SelectField
											name="cooling"
											label="Kühlung"
											keyItem="name"
											value={values.cooling ?? ''}
											options={affirmativeNegativeList}
										/>
									</Grid>
									<Grid item xs={12} md={12} lg={6} py={1}>
										<SelectField
											name="temperature_monitoring"
											label="Temperaturüberwachung"
											keyItem="name"
											value={values.temperature_monitoring ?? ''}
											options={affirmativeNegativeList}
										/>
									</Grid>
									<Grid item xs={12} md={12} lg={6} py={1}>
										<SelectField
											name="dark"
											label="Dunkel"
											keyItem="name"
											value={values.dark ?? ''}
											options={affirmativeNegativeList}
										/>
									</Grid>
									<Grid item xs={12} md={12} lg={6} py={1}>
										<SelectField
											name="other_cause"
											label="Sonstiger Anlass"
											keyItem="name"
											value={values.other_cause ?? ''}
											options={affirmativeNegativeList}
										/>
									</Grid>
								</Grid>
							</BoxWrap>
							<BoxWrap
								hasBackground={false}
								display="flex"
								justifyContent="end"
								gap="20px"
								my="20px"
							>
								<CustomButton
									startIcon={<ClearIcon />}
									label="Abbrechen"
									color="inherit"
									handleOnClick={() => {
										resetForm();
										navigate(-1);
									}}
								/>
								<CustomButton
									startIcon={<SaveIcon />}
									type="submit"
									disabled={isSubmitting}
									label={isSubmitting ? "Bestätigen..." : "Bestätigen"}
								/>
							</BoxWrap>
						</form>
					);
				}}
			</Formik>
		</>
	);
};

export default MicrobiologicalSettings;
